import React from 'react';
import { Crown } from 'lucide-react';
import { cn } from '../utils/cn';
import { Dialog } from './Dialog';
import { useSettings } from '../contexts/SettingsContext';
import { ensureValidHexColor } from '../utils/colors';
import {
  COMMON_ICONS,
  FOLDER_ICONS,
  MATERIAL_FOLDER_ICONS,
  MATERIAL_COMMON_ICONS,
  MATERIAL_CATEGORY_ICONS,
  MATERIAL_FILLED_ICONS,
  OTHER_ICONS,
  getIconComponent,
} from '../utils/icons';
import { useUpgradeFlow } from '../contexts/UpgradeFlowContext';
import { getPlatform } from '../infrastructure/platform';

interface FolderEditDialogProps {
  isOpen: boolean;
  initialName: string;
  initialIcon?: string;
  initialColor?: string;
  onClose: () => void;
  onConfirm: (name: string, icon: string, color: string) => void;
}

// Default folder color used when a folder has no color set yet. Neutral gray so
// it reads sensibly in both light and dark themes until the user picks one.
const DEFAULT_FOLDER_COLOR = '#6B7280';

// Preset swatches offered in the picker, laid out as two rows of eight to match
// the icon grid. Users can still choose any hex via the custom color input.
const FOLDER_COLORS = [
  '#6366F1',
  '#3B82F6',
  '#0EA5E9',
  '#06B6D4',
  '#14B8A6',
  '#22C55E',
  '#84CC16',
  '#EAB308',
  '#F59E0B',
  '#F97316',
  '#EF4444',
  '#F43F5E',
  '#EC4899',
  '#A855F7',
  '#8B5CF6',
  '#6B7280',
];

// The icon groups mirror the sets offered in Settings > Personalization so the
// two surfaces stay in sync. Names are resolved to components via
// getIconComponent, which also drives the sidebar rendering.
const ICON_GROUPS: { label: string; icons: string[] }[] = [
  { label: 'Common', icons: Object.keys(COMMON_ICONS) },
  { label: 'Folders', icons: Object.keys(FOLDER_ICONS) },
  { label: 'Material · Folders', icons: Object.keys(MATERIAL_FOLDER_ICONS) },
  { label: 'Material · Common', icons: Object.keys(MATERIAL_COMMON_ICONS) },
  { label: 'Material · Categories', icons: Object.keys(MATERIAL_CATEGORY_ICONS) },
  { label: 'Material · Filled', icons: Object.keys(MATERIAL_FILLED_ICONS) },
  { label: 'More', icons: Object.keys(OTHER_ICONS) },
];

export function FolderEditDialog({
  isOpen,
  initialName,
  initialIcon,
  initialColor,
  onClose,
  onConfirm,
}: FolderEditDialogProps) {
  const { settings } = useSettings();
  const isPremium = settings?.premium_status === 'paid';

  const [name, setName] = React.useState(initialName);
  const [icon, setIcon] = React.useState(initialIcon || 'Folder');
  const [color, setColor] = React.useState(
    ensureValidHexColor(initialColor || DEFAULT_FOLDER_COLOR, DEFAULT_FOLDER_COLOR)
  );

  // The dialog stays mounted and is only toggled via isOpen, so re-seed the
  // fields each time it opens (and when the target folder changes) instead of
  // relying on the initial useState values.
  React.useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setIcon(initialIcon || 'Folder');
      setColor(ensureValidHexColor(initialColor || DEFAULT_FOLDER_COLOR, DEFAULT_FOLDER_COLOR));
    }
  }, [isOpen, initialName, initialIcon, initialColor]);

  const { startUpgrade } = useUpgradeFlow();

  const openPaymentPage = () => {
    if (getPlatform().capabilities.upgradeFlow) {
      // The global upgrade dialog stacks above this one (NESTED_MODAL).
      startUpgrade();
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={() => onConfirm(name, icon, color)}
      title="Edit Folder"
      description="Rename this folder and choose an icon and color"
      confirmText="Save"
    >
      <div className="mt-4 space-y-4">
        {/* Name */}
        <div>
          <label htmlFor="folder-name-input" className="sr-only">
            Folder name
          </label>
          <input
            id="folder-name-input"
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Folder name"
            className={cn(
              'w-full px-3 py-2 text-sm rounded-md',
              'bg-white dark:bg-gray-900',
              'border border-gray-300 dark:border-gray-600',
              'focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400',
              'placeholder-gray-400 dark:placeholder-gray-500'
            )}
            autoFocus
            aria-label="Folder name"
          />
        </div>

        {/* Icon picker */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 inline-flex items-center">
              Folder icon
              <Crown className="w-3.5 h-3.5 ml-1 text-amber-500" />
            </span>
          </div>

          {!isPremium && (
            <button
              type="button"
              onClick={openPaymentPage}
              className={cn(
                'mb-2 inline-flex items-center gap-1 text-xs',
                'text-amber-600 dark:text-amber-400 hover:underline'
              )}
            >
              <Crown className="w-3.5 h-3.5" />
              Upgrade to premium to unlock custom folder icons
            </button>
          )}

          <div
            className={cn(
              'max-h-56 overflow-y-auto rounded-md border p-2',
              'border-gray-200 dark:border-gray-700',
              'bg-gray-50 dark:bg-gray-900/50',
              !isPremium && 'opacity-60'
            )}
            aria-disabled={!isPremium}
          >
            {ICON_GROUPS.map(group => (
              <div key={group.label} className="mb-3 last:mb-0">
                <div className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1 px-1">
                  {group.label}
                </div>
                <div className="grid grid-cols-8 gap-1">
                  {group.icons.map(iconName => {
                    const isSelected = icon === iconName;
                    return (
                      <button
                        key={`${group.label}-${iconName}`}
                        type="button"
                        disabled={!isPremium}
                        onClick={() => setIcon(iconName)}
                        title={iconName.replace(/^Md/, '')}
                        aria-label={iconName.replace(/^Md/, '')}
                        aria-pressed={isSelected}
                        className={cn(
                          'flex items-center justify-center h-8 w-8 rounded-md border transition-colors',
                          isSelected
                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300'
                            : 'border-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700',
                          !isPremium && 'cursor-not-allowed'
                        )}
                      >
                        {React.createElement(getIconComponent(iconName), {
                          className: 'w-4 h-4',
                          style: {
                            fontSize: iconName.startsWith('Md') ? '16px' : undefined,
                          },
                        })}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Color picker */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 inline-flex items-center">
              Folder color
              <Crown className="w-3.5 h-3.5 ml-1 text-amber-500" />
            </span>
          </div>

          {!isPremium && (
            <button
              type="button"
              onClick={openPaymentPage}
              className={cn(
                'mb-2 inline-flex items-center gap-1 text-xs',
                'text-amber-600 dark:text-amber-400 hover:underline'
              )}
            >
              <Crown className="w-3.5 h-3.5" />
              Upgrade to premium to unlock custom folder colors
            </button>
          )}

          <div
            className={cn(
              'rounded-md border p-2',
              'border-gray-200 dark:border-gray-700',
              'bg-gray-50 dark:bg-gray-900/50',
              !isPremium && 'opacity-60'
            )}
            aria-disabled={!isPremium}
          >
            <div className="grid grid-cols-8 gap-1">
              {FOLDER_COLORS.map(swatch => {
                const isSelected = color.toLowerCase() === swatch.toLowerCase();
                return (
                  <button
                    key={swatch}
                    type="button"
                    disabled={!isPremium}
                    onClick={() => setColor(swatch)}
                    title={swatch}
                    aria-label={swatch}
                    aria-pressed={isSelected}
                    className={cn(
                      'flex items-center justify-center h-8 w-8 rounded-md border-2 transition-transform',
                      isSelected
                        ? 'border-indigo-500 dark:border-indigo-300'
                        : 'border-transparent hover:scale-105',
                      !isPremium && 'cursor-not-allowed'
                    )}
                  >
                    <span
                      className="h-5 w-5 rounded-full border border-black/10 dark:border-white/10"
                      style={{ backgroundColor: swatch }}
                    />
                  </button>
                );
              })}
            </div>

            {/* Custom color */}
            <div className="mt-2 flex items-center gap-2 px-1">
              <label
                htmlFor="folder-color-input"
                className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500"
              >
                Custom
              </label>
              <input
                id="folder-color-input"
                type="color"
                disabled={!isPremium}
                value={color}
                onChange={e => setColor(ensureValidHexColor(e.target.value, DEFAULT_FOLDER_COLOR))}
                className={cn(
                  'h-7 w-10 rounded border border-gray-300 dark:border-gray-600 bg-transparent',
                  !isPremium ? 'cursor-not-allowed' : 'cursor-pointer'
                )}
                aria-label="Custom folder color"
              />
              <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                {color.toUpperCase()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
